import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import type { RegulationPost } from "../api/client";
import { useBackend } from "../app/BackendProvider";
import { describeError, useResource } from "../app/useResource";
import { useNetworkId } from "../config/ConfigProvider";
import { useLines } from "../config/LinesProvider";
import { sessionStore } from "../auth/session";

/**
 * Poste de régulation (F0.6, CDC §5.0). Un poste isole l'affichage sur un
 * sous-ensemble de lignes. La liste des postes, leurs noms et leur découpage
 * sont une donnée de configuration par réseau — jamais codés ici.
 *
 * L'isolement d'un poste vient de `lineIds` **ou**, s'il est vide, de
 * `operatorIds` résolus contre le référentiel des lignes (CDC §12.10 : « une
 * ligne peut relever de plusieurs postes… l'isolement d'un poste est strict »).
 * Sur Astuce, les postes réels isolent par opérateur (`lineIds` vide), pas par
 * liste de lignes — ne se fier qu'à `lineIds` masquerait l'isolement entier.
 *
 * Réseau sans notion de poste : `GET /posts` renvoie une liste vide →
 * `hasPosts === false`, aucune isolation, le sélecteur disparaît.
 *
 * Doit être monté sous `<LinesProvider>` (résolution des `operatorIds`).
 */

interface PostContextValue {
  posts: RegulationPost[];
  hasPosts: boolean;
  activePost: RegulationPost | null;
  setActivePost: (postId: string | null) => void;
  /**
   * Lignes sur lesquelles l'affichage est isolé. `null` = pas d'isolation
   * (aucun poste, ou poste couvrant tout le réseau).
   */
  activeLineIds: string[] | null;
}

const PostContext = createContext<PostContextValue | null>(null);

function storageKey(networkId: string): string {
  return `maestro.activePost.${networkId}`;
}

function readStoredPost(networkId: string): string | null {
  try {
    return localStorage.getItem(storageKey(networkId));
  } catch {
    return null;
  }
}

function writeStoredPost(networkId: string, postId: string | null): void {
  try {
    if (postId) localStorage.setItem(storageKey(networkId), postId);
    else localStorage.removeItem(storageKey(networkId));
  } catch {
    /* stockage bloqué : sélection en mémoire seulement */
  }
}

export function PostProvider({ children }: { children: ReactNode }) {
  const backend = useBackend();
  const networkId = useNetworkId();
  const lines = useLines();

  const postsRes = useResource((signal) => backend.api.listPosts(networkId, signal), [backend, networkId]);
  const posts = useMemo(() => postsRes.data ?? [], [postsRes.data]);

  const [activePostId, setActivePostId] = useState<string | null>(null);

  // Restaure la sélection persistée dès que la liste des postes est connue.
  // J.12 — défaut = « Général » (aucun poste, pas de `?post=`, réseau entier) ;
  // on ne restaure un poste que s'il a été explicitement choisi et existe encore.
  useEffect(() => {
    if (posts.length === 0) {
      setActivePostId(null);
      return;
    }
    const stored = readStoredPost(networkId);
    setActivePostId(stored && posts.some((p) => p.id === stored) ? stored : null);
  }, [posts, networkId]);

  const setActivePost = useCallback(
    (postId: string | null) => {
      setActivePostId(postId);
      writeStoredPost(networkId, postId);
      // Établit une session de poste (best-effort). B7.2 (v1.6) : aucun des
      // endpoints que le frontend appelle (lectures scopées `post=`, les deux
      // écritures régulateur) ne vérifie ce jeton — seul `/admin/**` l'exige,
      // hors périmètre écran (F5.2 différé). Sans UI de saisie de secret, cet
      // appel échoue simplement (401, secret vide) contre l'API réelle et ne
      // bloque rien ; gardé pour que la session s'établisse déjà quand un
      // besoin d'authentification poste apparaîtra ailleurs.
      if (postId) {
        backend.api
          .postSession({ kind: "post", post: postId, secret: "", networkId })
          .then((session) => sessionStore.set({ token: session.token, session }))
          .catch(() => {
            /* pas de session : voir commentaire ci-dessus */
          });
      } else {
        sessionStore.clear();
      }
    },
    [backend, networkId],
  );

  const activePost = useMemo(
    () => posts.find((p) => p.id === activePostId) ?? null,
    [posts, activePostId],
  );

  const value = useMemo<PostContextValue>(() => {
    let lineIds: string[] | null = null;
    if (activePost) {
      if (activePost.lineIds.length > 0) {
        lineIds = activePost.lineIds;
      } else if (activePost.operatorIds.length > 0) {
        const derived = lines.lines
          .filter((l) => activePost.operatorIds.includes(l.operatorId))
          .map((l) => l.id);
        lineIds = derived.length > 0 ? derived : null;
      }
    }
    return {
      posts,
      hasPosts: posts.length > 0,
      activePost,
      setActivePost,
      activeLineIds: lineIds,
    };
  }, [posts, activePost, setActivePost, lines.lines]);

  if (postsRes.error) {
    return (
      <p className="notification is-danger is-light m-4">
        Postes de régulation indisponibles : {describeError(postsRes.error)}
      </p>
    );
  }

  return <PostContext.Provider value={value}>{children}</PostContext.Provider>;
}

export function usePost(): PostContextValue {
  const value = useContext(PostContext);
  if (!value) throw new Error("usePost doit être utilisé sous <PostProvider>");
  return value;
}

/**
 * Paramètre `post` à passer aux endpoints scopés — l'identifiant du poste actif
 * dès qu'un poste est sélectionné, que son isolement se résolve ou non côté
 * frontend : c'est au serveur de décider, jamais une supposition du client.
 */
export function useActivePostParam(): string | undefined {
  const { activePost } = usePost();
  return activePost?.id;
}
